import React from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import withStyles from '@mui/styles/withStyles';

import Stack from '@mui/material/Stack';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import SvgIcon from '@mui/material/SvgIcon';

import GroupesIcon from '@mui/icons-material/Group';
import TransporteursIcon from '@mui/icons-material/LocalShipping';
import MotifResolutionIcon from '@mui/icons-material/Grain';
import CausePrincipaleIcon from '@mui/icons-material/ScatterPlot';
import ClientsIcon from '@mui/icons-material/TagFaces';
import FournisseursIcon from '@mui/icons-material/BusinessCenter';
import MarquesIcon from '@mui/icons-material/Copyright';
import FraisRetourIcon from '@mui/icons-material/Money';
import FraisRetourExclusIcon from '@mui/icons-material/MoneyOff';
import DecoteRetourExclusIcon from '@mui/icons-material/AlarmOff';
import ChargerContactExclusIcon from '@mui/icons-material/ContactPhone';
import RetourExclusIcon from '@mui/icons-material/CancelScheduleSend';
import RepresentantsIcon from '@mui/icons-material/FolderShared';
import MontantMiniRetourExclusIcon from '@mui/icons-material/ProductionQuantityLimits';
import PrioritairesIcon from '@mui/icons-material/PriorityHigh';
import DepotsIcon from '@mui/icons-material/Store';
import LitigesIcon from '@mui/icons-material/Gavel';
import CategoriesQualificationsIcon from '@mui/icons-material/Category';

import InterlocuteursSAVIcon from '@mui/icons-material/RecordVoiceOver';
import ConditionsSAVIcon from '@mui/icons-material/BrokenImage';
import UtilisateurIcon from '@mui/icons-material/PersonOutlineOutlined';
import ParametresIcon from '@mui/icons-material/Settings';
import ArborescenceIcon from '@mui/icons-material/AccountTreeOutlined';
import ExperimentalIcon from '@mui/icons-material/ScienceOutlined';

import AdministrationDrawerListItem from './AdministrationDrawerListItem';
import AdministrationDrawerSection from './AdministrationDrawerSection';
import AdministrationGroupes from './Groupes';
import AdministrationGroupe from './Groupe';

import AdministrationTransporteurs from './Transporteurs';
import AdministrationTransporteur from './Transporteur';
import AdministrationTransporteurComptes from './TransporteurComptes';
import AdministrationTransporteurCompte from './TransporteurCompte';

import AdministrationMotifsResolution from './MotifsResolution';
import AdministrationMotifResolution from './MotifResolution';

import AdministrationCausesPrincipales from './CausesPrincipales';
import AdministrationCausePrincipale from './CausePrincipale';

import AdministrationClients from './Clients';
import AdministrationClient from './Client';

import AdministrationFournisseurs from './Fournisseurs/AdministrationFournisseurs';
import AdministrationFournisseur from './Fournisseur';

import AdministrationMarques from './Marques/AdministrationMarques';

import AdministrationRepresentants from './Representants/AdministrationRepresentants';
import AdministrationRepresentant from './Representant';

import AdministrationFraisRetour from './FraisRetour';

import AdministrationFraisRetourExclus from './FraisRetourExclus';

import AdministrationDecoteRetourExclus from './DecoteRetourExclus';

import AdministrationMontantMiniRetourExclus from './MontantMiniRetourExclus';

import AdministrationRetourExclus from './RetourExclus';

import AdministrationInterlocuteursSAV from './InterlocuteursSAV';

import AdministrationConditionsSAV from './ConditionsSAV/AdministrationConditionsSAV';

import AdministrationSAVByFoussier from './SAVByFoussier/AdministrationSAVByFoussier';

import AdministrationParametres from './Parametres/AdministrationParametres';

import AdministrationUtilisateurs from './Utilisateurs/AdministrationUtilisateurs';
import AdministrationUtilisateur from './Utilisateur/AdministrationUtilisateurContainer';

import AdministrationArborescence from './Arborescence'

import AdministrationArticlesGestion from './ArticlesGestion/AdministrationArticlesGestion';

import AdministrationClientsTable from './Clients/AdministrationClientsTable';

import AdministrationUtilisateursTable from './Utilisateurs/AdministrationUtilisateursTable';

import AdministrationDepotsGestionTable from './DepotsGestion/AdministrationDepotsGestionTable';

import AdministrationArborescenceDeveloppeur from './Developpeur/AdministrationArborescenceDeveloppeur';

import AdministrationLitiges from './Litiges/AdministrationLitiges';

import AdministrationFeatureFlags from './FeatureFlags/AdministrationFeatureFlags';
import AdministrationRabbitMQManager from './RabbitMQManager/AdministrationRabbitMQManager';
import AdministrationIntegrationPiecesGestion from './IntegrationPiecesGestion/AdministrationIntegrationPiecesGestion';
import AdministrationMonitoringAvoir from './MonitoringAvoir/AdministrationMonitoringAvoir';
import AdministrationMonitoringBonsLivraison from './MonitoringBonsLivraison/AdministrationMonitoringBonsLivraison';
import AdministrationLectureRecent from './LectureRecent/AdministrationLectureRecent';
import AdministrationReclamationsSurBonDeVente from './ReclamationsSurBonDeVente/AdministrationReclamationsSurBonDeVente';
import AdministrationPackingLists from './PackingLists/AdministrationPackingLists';

import AdministrationCategoriesQualifications from './CategoriesQualifications/AdministrationCategoriesQualifications';

import AdministrationChargerContactExclus from './ChargerContactExclus/AdministrationChargerContactExclus';

const drawerWidth = 50;
const drawerFullWidth = 300;

const styles = theme => ({
    root: {
        padding: 0,
        flexGrow: 1,
        position: 'relative',
        display: 'flex'
    },

    content: {
        padding: 20,
        marginLeft: drawerWidth,
        backgroundColor: theme.palette.background.primary,
        width: '100%',
        transition: theme.transitions.create(['margin-left', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        })
    },

    contentClose: {
        overflowX: 'hidden',
        transition: theme.transitions.create(['margin-left', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        marginLeft: drawerFullWidth,
        width: `calc(100% - ${drawerFullWidth}px)`
    },

    drawerPaper: {
        overflow: 'visible',
        position: 'fixed',
        top: 72,
        flex: '0 0 auto',
        boxsizing: 'inherit',
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        })
    },

    drawerPaperClose: {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        width: drawerFullWidth
    },


    listItemIcon: {
        margin: 0,
        padding: 0
    },

    listItem: {
        paddingRight: 8,
        paddingLeft: 12
    },

    leftIcon: {
        marginRight: theme.spacing(1)
    },
    iconSmall: {
        fontSize: 20
    },
});

const children = {
    groupes: AdministrationGroupes,
    groupe: AdministrationGroupe,
    transporteurs: AdministrationTransporteurs,
    transporteur: AdministrationTransporteur,
    comptes: AdministrationTransporteurComptes,
    compte: AdministrationTransporteurCompte,
    motifsResolution: AdministrationMotifsResolution,
    motifResolution: AdministrationMotifResolution,
    causesPrincipales: AdministrationCausesPrincipales,
    causePrincipale: AdministrationCausePrincipale,
    clients: AdministrationClients,
    client: AdministrationClient,
    fournisseurs: AdministrationFournisseurs,
    fournisseur: AdministrationFournisseur,
    marques: AdministrationMarques,
    representants: AdministrationRepresentants,
    representant: AdministrationRepresentant,
    fraisRetour: AdministrationFraisRetour,
    fraisRetourExclus: AdministrationFraisRetourExclus,
    decoteRetourExclus: AdministrationDecoteRetourExclus,
    chargerContactExclus: AdministrationChargerContactExclus,
    montantMiniRetourExclus: AdministrationMontantMiniRetourExclus,
    retourExclus: AdministrationRetourExclus,
    interlocuteursSAV: AdministrationInterlocuteursSAV,
    conditionsSAV: AdministrationConditionsSAV,
    savByFoussier: AdministrationSAVByFoussier,
    parametres: AdministrationParametres,
    utilisateurs: AdministrationUtilisateurs,
    utilisateur: AdministrationUtilisateur,
    arborescence: AdministrationArborescence,
    articles: AdministrationArticlesGestion,
    developpeur: AdministrationArborescenceDeveloppeur,
    ['clients-table']: AdministrationClientsTable,
    ['utilisateurs-table']: AdministrationUtilisateursTable,
    ['depots-gestion-table']: AdministrationDepotsGestionTable,
    litiges: AdministrationLitiges,
    featureFlags: AdministrationFeatureFlags,
    categoriesQualifications: AdministrationCategoriesQualifications,
    rabbitMQManager: AdministrationRabbitMQManager,
    integrationPiecesGestion: AdministrationIntegrationPiecesGestion,
    monitoringAvoir: AdministrationMonitoringAvoir,
    monitoringBonsLivraison: AdministrationMonitoringBonsLivraison,
    lectureRecent: AdministrationLectureRecent,
    reclamationsSurBonDeVente: AdministrationReclamationsSurBonDeVente,
    packingLists: AdministrationPackingLists,
};

class AdministrationPage extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            expanded: true,
            arborescenceOpen : false,
            clientsOpen : false,
            savOpen: false,
            retourOpen: false,
            clotureOpen: false,
            adminOpen: false
        };
    }

    toggleExpanded = () => {
        this.setState({ expanded: !this.state.expanded });
    }

    toggleMenu = (menu) => {

        let newState = {
            ...this.state,
            arborescenceOpen: menu === 'arborescenceOpen' ? !this.state.arborescenceOpen : false,
            clientsOpen: menu === 'clientsOpen' ? !this.state.clientsOpen : false,
            savOpen: menu === 'savOpen' ? !this.state.savOpen : false,
            retourOpen: menu === 'retourOpen' ? !this.state.retourOpen : false,
            clotureOpen: menu === 'clotureOpen' ? !this.state.clotureOpen : false,
            adminOpen: menu === 'adminOpen' ? !this.state.adminOpen : false
        };

        this.setState(newState);
    }

    render() {
        let { classes, params, autoriseTout, autoriseConditionsSAV } = this.props;
        const { expanded } = this.state;

        let SAVByFoussierIcon = (
            <SvgIcon className={classNames(classes.leftIcon, classes.iconSmall)} style={{ width: '20px', height: '20px' }} viewBox="0 0 20 20" >
                <path d="M 4.601562 13.40625 C 4.726562 13.527344 4.867188 13.625 5.023438 13.699219 C 5.078125 14.742188 5.300781 15.765625 5.6875 16.734375 C 5.851562 17.121094 5.933594 17.535156 5.9375 17.957031 L 5.9375 19.0625 C 5.9375 19.582031 6.355469 20 6.875 20 L 10.625 20 C 11.144531 20 11.5625 19.582031 11.5625 19.0625 C 11.5625 18.527344 11.746094 18.007812 12.082031 17.589844 L 14.121094 15.046875 C 14.15625 15.007812 14.1875 14.96875 14.21875 14.929688 C 14.25 14.894531 14.28125 14.867188 14.308594 14.828125 C 14.335938 14.789062 14.367188 14.742188 14.398438 14.699219 L 14.476562 14.585938 C 14.503906 14.539062 14.523438 14.492188 14.546875 14.449219 C 14.570312 14.40625 14.589844 14.367188 14.609375 14.328125 C 14.628906 14.289062 14.648438 14.226562 14.667969 14.179688 L 14.714844 14.054688 C 14.714844 14.003906 14.742188 13.949219 14.757812 13.894531 C 14.769531 13.835938 14.777344 13.816406 14.789062 13.773438 C 14.796875 13.734375 14.789062 13.660156 14.808594 13.601562 C 14.832031 13.546875 14.808594 13.523438 14.828125 13.484375 C 14.847656 13.445312 14.828125 13.351562 14.828125 13.289062 C 14.828125 13.222656 14.828125 13.226562 14.828125 13.191406 C 14.824219 13.09375 14.816406 12.992188 14.800781 12.898438 L 14.773438 12.746094 L 14.460938 10.722656 C 14.425781 10.507812 14.363281 10.296875 14.273438 10.097656 C 14.269531 10.082031 14.265625 10.070312 14.257812 10.058594 C 13.976562 9.46875 13.476562 9.011719 12.867188 8.785156 L 11.890625 8.4375 L 12.976562 7.375 L 14.0625 6.25 C 14.136719 6.179688 14.242188 6.148438 14.34375 6.167969 C 15.929688 6.503906 17.511719 5.570312 17.980469 4.015625 C 18.105469 3.609375 18.144531 3.1875 18.097656 2.765625 C 18.070312 2.527344 17.90625 2.324219 17.679688 2.246094 C 17.449219 2.167969 17.195312 2.226562 17.027344 2.398438 L 16.160156 3.289062 C 16.101562 3.347656 16.019531 3.378906 15.9375 3.378906 C 15.855469 3.378906 15.773438 3.347656 15.714844 3.289062 L 14.839844 2.414062 C 14.78125 2.355469 14.746094 2.273438 14.75 2.1875 C 14.75 2.101562 14.785156 2.023438 14.84375 1.960938 L 15.714844 1.074219 C 15.886719 0.90625 15.941406 0.652344 15.863281 0.425781 C 15.78125 0.203125 15.582031 0.046875 15.347656 0.0195312 C 14.925781 -0.0234375 14.5 0.015625 14.097656 0.136719 C 12.550781 0.613281 11.625 2.191406 11.960938 3.773438 C 11.984375 3.875 11.953125 3.988281 11.875 4.0625 L 10.054688 5.84375 C 9.761719 5.457031 9.269531 5.273438 8.792969 5.378906 C 8.316406 5.484375 7.945312 5.851562 7.839844 6.328125 C 7.464844 6.222656 7.058594 6.296875 6.746094 6.535156 C 6.433594 6.769531 6.25 7.140625 6.25 7.53125 C 6.25 7.679688 6.277344 7.828125 6.328125 7.964844 L 6.253906 7.964844 C 5.855469 8.027344 5.515625 8.277344 5.335938 8.636719 C 5.152344 8.996094 5.15625 9.417969 5.339844 9.773438 C 4.695312 9.976562 4.328125 10.652344 4.507812 11.304688 C 4 11.921875 4.039062 12.828125 4.601562 13.398438 Z M 4.863281 11.898438 L 4.917969 11.9375 C 4.949219 11.964844 4.980469 11.992188 5.015625 12.015625 C 5.078125 12.058594 5.140625 12.09375 5.210938 12.125 L 6.460938 12.664062 C 6.515625 12.6875 6.574219 12.707031 6.632812 12.722656 L 6.378906 12.972656 C 6.339844 13.011719 6.296875 13.042969 6.25 13.074219 L 6.207031 13.101562 L 6.101562 13.15625 L 6.046875 13.179688 L 5.929688 13.214844 L 5.789062 13.214844 C 5.667969 13.222656 5.546875 13.207031 5.429688 13.171875 C 5.292969 13.128906 5.167969 13.050781 5.066406 12.949219 C 4.785156 12.667969 4.710938 12.242188 4.882812 11.882812 Z M 13.261719 9.75 C 13.566406 10.035156 13.765625 10.410156 13.832031 10.820312 L 14.183594 13.007812 C 14.195312 13.082031 14.203125 13.15625 14.207031 13.230469 C 14.226562 13.753906 14.03125 14.257812 13.664062 14.632812 L 13.644531 14.652344 L 11.59375 17.1875 C 11.167969 17.71875 10.933594 18.378906 10.9375 19.0625 C 10.9375 19.234375 10.796875 19.375 10.625 19.375 L 6.875 19.375 C 6.703125 19.375 6.5625 19.234375 6.5625 19.0625 L 6.5625 17.957031 C 6.558594 17.453125 6.449219 16.957031 6.25 16.492188 C 5.914062 15.652344 5.710938 14.761719 5.644531 13.859375 L 5.695312 13.859375 C 5.761719 13.859375 5.828125 13.851562 5.890625 13.839844 C 5.917969 13.84375 5.941406 13.84375 5.96875 13.839844 C 6.042969 13.828125 6.117188 13.808594 6.1875 13.78125 L 6.265625 13.757812 C 6.34375 13.726562 6.421875 13.6875 6.492188 13.644531 L 6.542969 13.617188 C 6.632812 13.5625 6.714844 13.496094 6.789062 13.421875 L 8.542969 11.699219 C 8.746094 11.644531 8.929688 11.539062 9.074219 11.386719 L 9.09375 11.371094 C 9.140625 11.324219 9.183594 11.273438 9.222656 11.222656 C 9.324219 11.082031 9.394531 10.921875 9.429688 10.753906 C 9.527344 10.753906 9.625 10.746094 9.71875 10.726562 C 9.945312 10.945312 10.21875 11.117188 10.519531 11.21875 L 11.261719 11.503906 C 11.433594 11.5625 11.554688 11.710938 11.574219 11.890625 C 11.597656 12.109375 11.46875 12.320312 11.261719 12.398438 C 9.585938 13.023438 8.640625 14.097656 8.449219 15.582031 C 8.421875 15.75 8.539062 15.910156 8.710938 15.9375 L 8.75 15.9375 C 8.90625 15.9375 9.042969 15.820312 9.0625 15.664062 C 9.230469 14.414062 10.023438 13.535156 11.480469 12.988281 C 11.957031 12.804688 12.246094 12.324219 12.1875 11.820312 C 12.136719 11.421875 11.871094 11.082031 11.496094 10.9375 L 10.742188 10.625 C 10.472656 10.53125 10.230469 10.363281 10.050781 10.140625 C 10.011719 10.09375 9.972656 10.042969 9.945312 9.988281 C 9.925781 9.960938 9.90625 9.929688 9.890625 9.902344 C 9.855469 9.84375 9.828125 9.785156 9.804688 9.722656 C 9.789062 9.679688 9.773438 9.640625 9.757812 9.597656 C 9.746094 9.554688 9.738281 9.519531 9.726562 9.480469 C 9.71875 9.445312 9.710938 9.386719 9.707031 9.339844 C 9.703125 9.300781 9.703125 9.257812 9.707031 9.21875 C 9.703125 9.207031 9.703125 9.195312 9.707031 9.179688 C 9.703125 9.125 9.703125 9.066406 9.707031 9.007812 C 9.703125 8.988281 9.703125 8.96875 9.707031 8.945312 C 9.703125 8.929688 9.703125 8.914062 9.707031 8.898438 C 9.71875 8.808594 9.738281 8.722656 9.765625 8.640625 C 9.820312 8.480469 9.996094 8.390625 10.15625 8.445312 L 12.632812 9.375 C 12.863281 9.457031 13.078125 9.585938 13.261719 9.75 Z M 12.304688 4.492188 C 12.527344 4.269531 12.621094 3.949219 12.554688 3.640625 C 12.472656 3.265625 12.484375 2.871094 12.582031 2.5 C 12.792969 1.660156 13.433594 0.988281 14.261719 0.738281 C 14.589844 0.636719 14.933594 0.605469 15.273438 0.644531 L 14.398438 1.53125 C 14.222656 1.707031 14.125 1.945312 14.125 2.195312 C 14.125 2.445312 14.222656 2.683594 14.398438 2.859375 L 15.285156 3.75 C 15.65625 4.105469 16.242188 4.105469 16.613281 3.75 L 17.5 2.847656 C 17.535156 3.183594 17.507812 3.523438 17.410156 3.847656 C 17.160156 4.691406 16.480469 5.335938 15.625 5.542969 C 15.25 5.644531 14.855469 5.652344 14.476562 5.570312 C 14.164062 5.503906 13.84375 5.597656 13.617188 5.820312 L 11.398438 8.007812 L 11.398438 7.992188 C 11.371094 7.910156 11.335938 7.828125 11.289062 7.757812 L 10.40625 6.378906 Z M 8.726562 6.054688 C 9.019531 5.871094 9.40625 5.957031 9.589844 6.25 L 9.742188 6.492188 L 10.679688 7.972656 L 10.367188 7.855469 C 10.132812 7.773438 9.875 7.792969 9.652344 7.902344 C 9.621094 7.917969 9.59375 7.9375 9.566406 7.957031 L 8.457031 6.722656 C 8.398438 6.46875 8.507812 6.203125 8.726562 6.0625 Z M 7.058594 7.085938 C 7.175781 6.964844 7.335938 6.898438 7.503906 6.898438 C 7.671875 6.898438 7.832031 6.964844 7.949219 7.085938 L 9.175781 8.449219 C 9.160156 8.5 9.148438 8.550781 9.132812 8.601562 C 9.007812 9.085938 9.054688 9.597656 9.261719 10.050781 L 7.558594 8.460938 L 7.058594 7.960938 C 6.816406 7.714844 6.816406 7.320312 7.058594 7.078125 Z M 5.9375 8.84375 C 6.140625 8.5625 6.53125 8.5 6.808594 8.703125 L 7.121094 8.941406 L 8.824219 10.523438 C 8.816406 10.636719 8.777344 10.746094 8.710938 10.835938 C 8.6875 10.867188 8.664062 10.890625 8.640625 10.914062 C 8.550781 10.992188 8.445312 11.046875 8.328125 11.066406 C 8.214844 11.089844 8.097656 11.078125 7.992188 11.039062 L 6.300781 9.851562 L 6.066406 9.679688 C 5.8125 9.476562 5.753906 9.113281 5.9375 8.84375 Z M 5.128906 10.71875 C 5.226562 10.492188 5.453125 10.34375 5.703125 10.34375 C 5.789062 10.34375 5.875 10.359375 5.953125 10.394531 L 5.976562 10.394531 L 7.558594 11.503906 C 7.558594 11.585938 7.542969 11.667969 7.511719 11.742188 C 7.457031 11.902344 7.339844 12.027344 7.1875 12.097656 C 7.035156 12.160156 6.863281 12.160156 6.710938 12.097656 L 5.460938 11.554688 C 5.421875 11.539062 5.386719 11.519531 5.355469 11.496094 C 5.277344 11.433594 5.214844 11.355469 5.167969 11.261719 L 5.148438 11.226562 C 5.074219 11.070312 5.070312 10.890625 5.132812 10.726562 Z M 5.128906 10.71875 " />
                <path d="M 7.984375 3.679688 C 7.949219 3.757812 7.980469 3.851562 8.054688 3.890625 L 8.679688 4.203125 C 8.703125 4.214844 8.726562 4.21875 8.75 4.21875 C 8.824219 4.21875 8.886719 4.167969 8.902344 4.097656 C 8.917969 4.027344 8.882812 3.953125 8.820312 3.921875 L 8.195312 3.609375 C 8.117188 3.574219 8.023438 3.605469 7.984375 3.679688 Z M 7.984375 3.679688 " />
                <path d="M 9.0625 3.28125 C 9.105469 3.28125 9.144531 3.265625 9.171875 3.234375 C 9.203125 3.207031 9.21875 3.167969 9.21875 3.125 C 9.21875 3.082031 9.203125 3.042969 9.171875 3.015625 L 8.546875 2.390625 C 8.488281 2.332031 8.386719 2.332031 8.328125 2.390625 C 8.269531 2.449219 8.269531 2.550781 8.328125 2.609375 L 8.953125 3.234375 C 8.980469 3.265625 9.019531 3.28125 9.0625 3.28125 Z M 9.0625 3.28125 " />
                <path d="M 10.15625 3.125 L 10.15625 2.8125 C 10.15625 2.726562 10.085938 2.65625 10 2.65625 C 9.914062 2.65625 9.84375 2.726562 9.84375 2.8125 L 9.84375 3.125 C 9.84375 3.210938 9.914062 3.28125 10 3.28125 C 10.085938 3.28125 10.15625 3.210938 10.15625 3.125 Z M 10.15625 3.125 " />
            </SvgIcon>
        );

        let component = params.component;
        let subcomponent = params.subcomponent;
        let id = Number(params.id);
        let code = params.id;
        let subid = Number(params.subid);
        let title;
        let Content = children[component];

        //console.log('component', component);
        //console.log('id', id);
        //console.log('subid', subid);

        switch (component) {
            case 'groupes':
                title = 'Groupes';
                if (id > 0) {
                    title = `Groupe ${id}`;
                    component = 'groupe';
                    Content = children[component];
                }
                break;
            case 'motifsResolution':
                title = 'Motifs résolution';
                if (id > 0) {
                    title = `Motif résolution ${id}`;
                    component = 'motifResolution';
                    Content = children[component];
                }
                break;
            case 'causesPrincipales':
                title = 'Causes principales';
                if (id > 0) {
                    title = `Cause principale ${id}`;
                    component = 'causePrincipale';
                    Content = children[component];
                }
                break;
            case 'transporteurs':
                title = 'Transporteurs';
                if (id > 0) {
                    switch (subcomponent) {
                        case 'comptes':
                            if (subid > 0) {
                                title = `Compte ${subid} du transporteur ${id}`;
                                subcomponent = 'compte';
                                Content = children[subcomponent];
                            }
                            else {
                                title = `Comptes du transporteur ${id}`;
                                subcomponent = 'comptes';
                                Content = children[subcomponent];
                            }
                            break;
                        default:
                            title = `Transporteur ${id}`;
                            component = 'transporteur';
                            Content = children[component];
                            break;
                    }
                }
                break;
            case 'clients':
                title = 'clients';
                if (id > 0) {
                    title = `Client ${id}`;
                    component = 'client';
                    Content = children[component];
                }
                break;
            case 'fournisseurs':
                title = 'fournisseurs';
                if (id > 0) {
                    title = `Fournisseur ${id}`;
                    component = 'fournisseur';
                    Content = children[component];
                }
                break;
            case 'marques':
                title = 'marques';
                if (id > 0) {
                    title = `Marque ${id}`;
                    component = 'marque';
                    Content = children[component];
                }
                break;
            case 'fraisRetour':
                title = 'Frais de retour';
                break;
            case 'fraisRetourExclus':
                title = 'Exclusions des frais de retour';
                break;
            case 'decoteRetourExclus':
                title = 'Exclusions des décôtes au retour';
                break;
            case 'chargerContactExclus':
                title = 'Exclusions du chargement des contacts';
                break;
            case 'retourExclus':
                title = 'Exclusions de retour';
                break;
            case 'representants':
                title = 'representants';
                if (code !== undefined && code !== '') {
                    id = 0;
                    title = `Representant ${code}`;
                    component = 'representant';
                    Content = children[component];
                }
                break;

            case 'interlocuteursSAV':
                title = 'Interlocuteurs S.A.V.';
                break;

            case 'conditionsSAV':
                title = 'Conditions S.A.V.';
                break;

            case 'savByFoussier':
                title = 'S.A.V. by Foussier';
                break;

            case 'parametres':
                title = 'Paramètres';
                break;

            case 'montantMiniRetourExclus':
                title = 'Exclusions des montants mini au retour';
                break;

            case 'utilisateurs':
                title = 'Utilisateurs';
                if (id > 0) {
                    title = `Utilisateur ${id}`;
                    component = 'utilisateur';
                    Content = children[component];
                }
                break;

            case 'arborescence':
                title = 'Arborescence';
                break;

            case 'articles':
                title = 'Articles';
                break;

            case 'clients-table':
                title = 'clientsTable';
                break;

            case 'utilisateurs-table':
                title = 'utilisateursTable';
                break;

            case 'depots-gestion-table':
                title = 'depotsGestionTable';
                break;

            case 'litiges':
                title = 'Litiges';
                break;

            case 'developpeur':
                title = 'developpeur';
                break;

            case 'featureFlags':
                title = 'Feature Flags';
                break;
                
            case 'rabbitMQManager':
                title = 'Gestion des files RabbitMQ';
                break;

            case 'integrationPiecesGestion':
                title = 'Intégration pièces gestion';
                break;

            case 'monitoringAvoir':
                title = 'Monitoring des avoirs';
                break;

            case 'monitoringBonsLivraison':
                title = 'Monitoring bons livraison';
                break;

            case 'lectureRecent':
                title = 'Lecture RECENT';
                break;

            case 'reclamationsSurBonDeVente':
                title = 'Réclamations sur bon de vente';
                break;

            case 'packingLists':
                title = 'Packing lists';
                break;

            case 'categoriesQualifications':
                title = 'Catégories & qualifications';
                break;

            default:
                if (autoriseTout) {
                    title = 'Administration';
                    component = 'arborescence';
                    Content = children[component];
                }
                else if (autoriseConditionsSAV) {
                    title = 'Conditions S.A.V.';
                    component = 'conditionsSAV';
                    Content = children[component];
                }
                break;
        }

        let arborescenceCollapseIn = this.state.arborescenceOpen || ['arborescence', 'articles'].indexOf(component) >= 0;  
        let clientsCollapseIn = this.state.clientsOpen || ['clients', 'client', 'clients-table', 'chargerContactExclus'].indexOf(component) >= 0;
        let clotureCollapseIn = this.state.clotureOpen || ['causesPrincipales', 'causePrincipale', 'motifsResolution', 'motifResolution'].indexOf(component) >= 0;  
        let retourCollapseIn = this.state.retourOpen || ['fraisRetour', 'fraisRetourExclus', 'decoteRetourExclus', 'retourExclus', 'montantMiniRetourExclus'].indexOf(component) >= 0;
        let savCollapseIn = this.state.savOpen || ['interlocuteursSAV', 'conditionsSAV', 'savByFoussier'].indexOf(component) >= 0;
        let adminCollapseIn = this.state.adminOpen || ['featureFlags', 'rabbitMQManager', 'integrationPiecesGestion', 'monitoringAvoir', 'monitoringBonsLivraison', 'lectureRecent', 'packingLists'].indexOf(component) >= 0;

        return (
            <div className={classes.root}>
                <Helmet>
                    <title>{title}</title>
                </Helmet>

                <Drawer variant="permanent" classes={{ paper: classNames(classes.drawerPaper, expanded && classes.drawerPaperClose) }}>

                    <Stack spacing={0} sx={{ pb: 15 }}>

                        <div className={classNames(expanded && classes.drawerPaperClose)}>
                            {!expanded && (
                                <ListItem onClick={this.toggleExpanded} className={classes.listItem}>
                                    <ListItemIcon >
                                        <SvgIcon className={classes.listItemIcon} style={{ width: '24px', height: '24px' }} viewBox="0 0 24 24">
                                            <path d="M5.59,7.41L7,6L13,12L7,18L5.59,16.59L10.17,12L5.59,7.41M11.59,7.41L13,6L19,12L13,18L11.59,16.59L16.17,12L11.59,7.41Z" />
                                        </SvgIcon>
                                    </ListItemIcon>
                                </ListItem>
                            )}

                            {expanded && (
                                <ListItem onClick={this.toggleExpanded} className={classes.listItem}>
                                    <ListItemIcon>
                                        <SvgIcon className={classes.listItemIcon} style={{ width: '24px', height: '24px' }} viewBox="0 0 24 24">
                                            <path d="M18.41,7.41L17,6L11,12L17,18L18.41,16.59L13.83,12L18.41,7.41M12.41,7.41L11,6L5,12L11,18L12.41,16.59L7.83,12L12.41,7.41Z" />
                                        </SvgIcon>
                                    </ListItemIcon>
                                </ListItem>
                            )}
                            <Divider />
                        </div>

                        <AdministrationDrawerSection title="Arborescence" open={arborescenceCollapseIn} onToggle={() => this.toggleMenu("arborescenceOpen")} expanded={expanded} allowed={autoriseTout}>
                            <AdministrationDrawerListItem
                                title="Arborescence"
                                href="/administration/arborescence"
                                icon={<Tooltip title="Arborescence"><ArborescenceIcon /></Tooltip>}
                                selected={component === 'arborescence'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Articles"
                                href="/administration/articles"
                                icon={<Tooltip title="Articles"><ExperimentalIcon /></Tooltip>}
                                selected={component === 'articles'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                        </AdministrationDrawerSection>


                        <AdministrationDrawerSection title="Clients" open={clientsCollapseIn} onToggle={() => this.toggleMenu("clientsOpen")} expanded={expanded} allowed={autoriseTout}>
                            <AdministrationDrawerListItem
                                title="Clients"
                                href="/administration/clients-table"
                                icon={<Tooltip title="Clients"><ClientsIcon /></Tooltip>}
                                selected={component === 'clients-table'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Clients prioritaires"
                                href="/administration/clients"
                                icon={<Tooltip title="Clients"><PrioritairesIcon /></Tooltip>}
                                selected={component === 'clients' || component === 'client'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Exclusions du chargement des contacts"
                                href="/administration/chargerContactExclus"
                                icon={<Tooltip title="Exclusions du chargement des contacts"><ChargerContactExclusIcon /></Tooltip>}
                                selected={component === 'chargerContactExclus'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                        </AdministrationDrawerSection>

                        <AdministrationDrawerSection title="Clôture" open={clotureCollapseIn} onToggle={() => this.toggleMenu("clotureOpen")} expanded={expanded} allowed={autoriseTout}>
                            <AdministrationDrawerListItem
                                title="Causes principales"
                                href="/administration/causesPrincipales"
                                icon={<Tooltip title="Causes principales"><CausePrincipaleIcon /></Tooltip>}
                                selected={component === 'causesPrincipales' || component === 'causePrincipale'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Motifs de résolution"
                                href="/administration/motifsResolution"
                                icon={<Tooltip title="Motifs de résolution"><MotifResolutionIcon /></Tooltip>}
                                selected={component === 'motifsResolution' || component === 'motifResolution'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                        </AdministrationDrawerSection>



                        <AdministrationDrawerSection title="Retour" open={retourCollapseIn} onToggle={() => this.toggleMenu("retourOpen")} expanded={expanded} allowed={autoriseTout}>
                            <AdministrationDrawerListItem
                                title="Frais de retour"
                                href="/administration/fraisRetour"
                                icon={<Tooltip title="Frais de retour"><FraisRetourIcon /></Tooltip>}
                                selected={component === 'fraisRetour'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Exclusions des frais de retour"
                                href="/administration/fraisRetourExclus"
                                icon={<Tooltip title="Exclusions des frais de retour"><FraisRetourExclusIcon /></Tooltip>}
                                selected={component === 'fraisRetourExclus'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Exclusions des décôtes au retour"
                                href="/administration/decoteRetourExclus"
                                icon={<Tooltip title="Exclusions des décôtes au retour"><DecoteRetourExclusIcon /></Tooltip>}
                                selected={component === 'decoteRetourExclus'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Exclusions de retour"
                                href="/administration/retourExclus"
                                icon={<Tooltip title="Exclusions de retour"><RetourExclusIcon /></Tooltip>}
                                selected={component === 'retourExclus'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Exclusions des montants mini au retour"
                                href="/administration/montantMiniRetourExclus"
                                icon={<Tooltip title="Exclusions des montants mini au retour"><MontantMiniRetourExclusIcon /></Tooltip>}
                                selected={component === 'montantMiniRetourExclus'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                        </AdministrationDrawerSection>

                        <AdministrationDrawerSection title="S.A.V." open={savCollapseIn} onToggle={() => this.toggleMenu("savOpen")} expanded={expanded} allowed={autoriseTout || autoriseConditionsSAV}>
                            <AdministrationDrawerListItem
                                title="Interlocuteurs S.A.V."
                                href="/administration/interlocuteursSAV"
                                icon={<Tooltip title="Interlocuteurs S.A.V."><InterlocuteursSAVIcon /></Tooltip>}
                                selected={component === 'interlocuteursSAV'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Conditions S.A.V."
                                href="/administration/conditionsSAV"
                                icon={<Tooltip title="Conditions S.A.V."><ConditionsSAVIcon /></Tooltip>}
                                selected={component === 'conditionsSAV'}
                                expanded={expanded}
                                allowed={autoriseTout || autoriseConditionsSAV}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="S.A.V. By Foussier"
                                href="/administration/savByFoussier"
                                icon={<Tooltip title="S.A.V. By Foussier">{SAVByFoussierIcon}</Tooltip>}
                                selected={component === 'savByFoussier'}
                                expanded={expanded}
                                allowed={autoriseTout || autoriseConditionsSAV}
                                depth={1}
                            />
                        </AdministrationDrawerSection>


                        <AdministrationDrawerListItem
                            title="Catégories & qualifications"
                            href="/administration/categoriesQualifications"
                            icon={<Tooltip title="Catégories & qualifications"><CategoriesQualificationsIcon /></Tooltip>}
                            selected={component === 'categoriesQualifications'}
                            expanded={expanded}
                            allowed={autoriseTout}
                        />

                        <AdministrationDrawerListItem
                            title="Dépots"
                            href="/administration/depots-gestion-table"
                            icon={<Tooltip title="Dépots"><DepotsIcon /></Tooltip>}
                            selected={component === 'depotsGestionTable'}
                            expanded={expanded}
                            allowed={autoriseTout}
                        />

                        <AdministrationDrawerListItem
                            title="Fournisseurs"
                            href="/administration/fournisseurs"
                            icon={<Tooltip title="Fournisseurs"><FournisseursIcon /></Tooltip>}
                            selected={component === 'fournisseurs' || component === 'fournisseur'}
                            expanded={expanded}
                            allowed={autoriseTout}
                        />

                        <AdministrationDrawerListItem
                            title="Groupes"
                            href="/administration/groupes"
                            icon={<Tooltip title="Groupes de traitement"><GroupesIcon /></Tooltip>}
                            selected={component === 'groupes' || component === 'groupe'}
                            expanded={expanded}
                            allowed={autoriseTout}
                        />

                        <AdministrationDrawerListItem
                            title="Litiges"
                            href="/administration/litiges"
                            icon={<Tooltip title="Litiges"><LitigesIcon /></Tooltip>}
                            selected={component === 'litiges'}
                            expanded={expanded}
                            allowed={autoriseTout}
                        />

                        <AdministrationDrawerListItem
                            title="Marques"
                            href="/administration/marques"
                            icon={<Tooltip title="Marques"><MarquesIcon /></Tooltip>}
                            selected={component === 'marques' || component === 'marque'}
                            expanded={expanded}
                            allowed={autoriseTout || autoriseConditionsSAV}
                        />

                        <AdministrationDrawerListItem
                            title="Paramètres"
                            href="/administration/parametres"
                            icon={<Tooltip title="Paramètres"><ParametresIcon /></Tooltip>}
                            selected={component === 'parametres'}
                            expanded={expanded}
                            allowed={autoriseTout}
                        />


                        <AdministrationDrawerListItem
                            title="Representants"
                            href="/administration/representants"
                            icon={<Tooltip title="Representants"><RepresentantsIcon /></Tooltip>}
                            selected={component === 'representants' || component === 'representant'}
                            expanded={expanded}
                            allowed={autoriseTout}
                        />


                        <AdministrationDrawerListItem
                            title="Transporteurs"
                            href="/administration/transporteurs"
                            icon={<Tooltip title="Transporteurs"><TransporteursIcon /></Tooltip>}
                            selected={component === 'transporteurs' || component === 'transporteur'}
                            expanded={expanded}
                            allowed={autoriseTout}
                        />
                        {/* <AdministrationDrawerListItem
                            title="Utilisateurs"
                            href="/administration/utilisateurs"
                            icon={<Tooltip title="Utilisateurs"><UtilisateurIcon /></Tooltip>}
                            selected={component === 'utilisateurs' || component === 'utilisateur'}
                            expanded={expanded}
                            allowed={autoriseTout}
                        /> */}

                        <AdministrationDrawerListItem
                            title="Utilisateurs"
                            href="/administration/utilisateurs-table"
                            icon={<Tooltip title="Utilisateurs"><UtilisateurIcon /></Tooltip>}
                            selected={component === 'utilisateursTable' || component === 'utilisateur'}
                            expanded={expanded}
                            allowed={autoriseTout}
                        />


                        <AdministrationDrawerSection title="Expérimental" open={adminCollapseIn} onToggle={() => this.toggleMenu("adminOpen")} expanded={expanded} allowed={autoriseTout}>
                            <AdministrationDrawerListItem
                                title="Feature flags"
                                href="/administration/featureFlags"
                                icon={<Tooltip title="Feature flags"><ExperimentalIcon /></Tooltip>}
                                selected={component === 'featureFlags'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Gestion des files RabbitMQ"
                                href="/administration/rabbitMQManager"
                                icon={<Tooltip title="Gestion des files RabbitMQ"><ExperimentalIcon /></Tooltip>}
                                selected={component === 'rabbitMQManager'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Intégration pièces gestion"
                                href="/administration/integrationPiecesGestion"
                                icon={<Tooltip title="Intégration pièces gestion"><ExperimentalIcon /></Tooltip>}
                                selected={component === 'integrationPiecesGestion'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Monitoring des avoirs"
                                href="/administration/monitoringAvoir"
                                icon={<Tooltip title="Monitoring des avoirs"><ExperimentalIcon /></Tooltip>}
                                selected={component === 'monitoringAvoir'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Monitoring bons livraison"
                                href="/administration/monitoringBonsLivraison"
                                icon={<Tooltip title="Monitoring bons livraison (expérimental)"><ExperimentalIcon /></Tooltip>}
                                selected={component === 'monitoringBonsLivraison'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Lecture RECENT"
                                href="/administration/lectureRecent"
                                icon={<Tooltip title="Lecture RECENT"><ExperimentalIcon /></Tooltip>}
                                selected={component === 'lectureRecent'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Réclamations sur bon de vente"
                                href="/administration/reclamationsSurBonDeVente"
                                icon={<Tooltip title="Réclamations sur bon de vente"><ExperimentalIcon /></Tooltip>}
                                selected={component === 'reclamationsSurBonDeVente'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                            <AdministrationDrawerListItem
                                title="Packing lists"
                                href="/administration/packingLists"
                                icon={<Tooltip title="Packing lists"><ExperimentalIcon /></Tooltip>}
                                selected={component === 'packingLists'}
                                expanded={expanded}
                                allowed={autoriseTout}
                                depth={1}
                            />
                        </AdministrationDrawerSection>


                    </Stack>

                </Drawer>

                <div className={classNames(classes.content, expanded && classes.contentClose)}>
                    <Content id={id} subid={subid} code={code} />
                </div>
            </div>
        );
    }
}

AdministrationPage.propTypes = {
    classes: PropTypes.object.isRequired,
    params: PropTypes.object.isRequired,
    autoriseTout: PropTypes.bool.isRequired,
    autoriseConditionsSAV: PropTypes.bool.isRequired
};

export default withStyles(styles)(AdministrationPage);